import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';
import { useState } from 'react';

export default function STRCohostingPage() {
  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/property-assessment');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#F0F0F0] to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-xs font-semibold text-[#882629] uppercase tracking-widest mb-6">
            Service Details
          </div>
          <h1 className="font-['Fraunces'] text-5xl lg:text-6xl font-bold text-[#505A5A] mb-8">
            Short-Term Rental Co-Hosting
          </h1>
          <p className="text-xl text-[#505A5A]/80 leading-relaxed max-w-3xl">
            For qualifying owner-occupied homes, we handle everything from licensing setup to guest communication, pricing optimization, and day-to-day operations. You earn income while we do the work.
          </p>
        </div>
      </section>

      {/* What We Handle */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            What we handle for you
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: 'ri-file-text-line',
                title: 'Licensing and compliance',
                description: 'We complete all city licensing applications, ensure your property meets safety and insurance requirements, and keep you compliant with local regulations.'
              },
              {
                icon: 'ri-image-edit-line',
                title: 'Professional listing creation',
                description: 'High-quality photos, compelling descriptions, and optimized titles that attract guests and maximize bookings on Airbnb and other platforms.'
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Dynamic pricing strategy',
                description: 'We adjust your nightly rates based on demand, seasonality, local events, and competitor pricing to maximize your revenue.'
              },
              {
                icon: 'ri-calendar-schedule-line',
                title: 'Calendar management',
                description: 'We manage your availability, sync calendars across platforms, and coordinate bookings to avoid double-bookings and maximize occupancy.'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Guest communication',
                description: 'We handle all guest inquiries, booking confirmations, check-in instructions, and support requests—24/7.'
              },
              {
                icon: 'ri-key-2-line',
                title: 'Check-in and check-out coordination',
                description: 'We provide guests with detailed check-in instructions, smart lock codes, and ensure smooth arrivals and departures.'
              },
              {
                icon: 'ri-broom-line',
                title: 'Cleaning and maintenance coordination',
                description: 'We schedule professional cleaning between guests and coordinate any maintenance or repairs needed to keep your property in top condition.'
              },
              {
                icon: 'ri-star-smile-line',
                title: 'Review management',
                description: 'We respond to guest reviews, encourage positive feedback, and maintain your property\'s reputation and ratings.'
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Performance reporting',
                description: 'Monthly reports showing bookings, revenue, expenses, occupancy rates, and insights to help you understand your property\'s performance.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-[#882629] text-4xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Requirements */}
      <section className="py-20 bg-[#F0F0F0]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-8">
            Eligibility requirements
          </h2>
          <p className="text-lg text-[#505A5A]/70 mb-10 leading-relaxed">
            Most cities require short-term rentals to be owner-occupied, meaning you must live in the property as your primary residence. Here's what you need to qualify:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: 'ri-home-smile-line',
                title: 'Primary residence',
                description: 'The property must be your principal residence where you live for the majority of the year.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'City licensing',
                description: 'Your property must be eligible for a short-term rental license in your municipality.'
              },
              {
                icon: 'ri-building-line',
                title: 'Strata approval (if applicable)',
                description: 'If you own a condo or townhouse, your strata bylaws must permit short-term rentals.'
              },
              {
                icon: 'ri-file-shield-2-line',
                title: 'Insurance coverage',
                description: 'You must have appropriate insurance that covers short-term rental activity.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-[#882629] text-4xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            Why choose our co-hosting service
          </h2>
          <div className="space-y-6">
            <div className="bg-[#F0F0F0] rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Local expertise
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                We specialize in BC and Alberta markets and understand the unique regulations, guest expectations, and seasonal trends in each city we serve.
              </p>
            </div>
            <div className="bg-[#F0F0F0] rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Hands-off income
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                You don't need to manage bookings, respond to guests, or coordinate cleanings. We handle everything so you can enjoy the income without the hassle.
              </p>
            </div>
            <div className="bg-[#F0F0F0] rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Maximized revenue
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                Our dynamic pricing and optimization strategies consistently outperform self-managed listings, often increasing revenue by 20-40%.
              </p>
            </div>
            <div className="bg-[#F0F0F0] rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Peace of mind
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                We ensure compliance, maintain your property, and provide 24/7 guest support so you never have to worry about issues or emergencies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Proven Results */}
      <section className="py-24 bg-[#F9F5F0]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-xs font-semibold text-[#882629] uppercase tracking-widest mb-4">
              Real Results, Not Theory
            </div>
            <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
              Proof our co-hosting works
            </h2>
            <p className="text-lg text-[#505A5A]/70 max-w-2xl mx-auto leading-relaxed">
              We let the numbers speak. Here&apos;s what happens when a property transitions from self-managed to our co-hosting service.
            </p>
          </div>

          {/* Case Study Card */}
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden mb-16">
            <div className="grid lg:grid-cols-2">
              {/* Listing Screenshot */}
              <div className="relative">
                <div className="w-full h-full min-h-[400px]">
                  <img
                    src="https://public.readdy.ai/ai/img_res/5d8a8527931fd105a9688a48caa37dcf.jpg"
                    alt="Vancouver 1-bedroom Airbnb listing managed by our co-hosting team"
                    title="Vancouver STR co-hosting listing - professional management"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-md">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <i key={s} className="ri-star-fill text-[#E8A838] text-sm"></i>
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-[#505A5A]">4.94</span>
                    <span className="text-xs text-[#505A5A]/60">· 87 reviews</span>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 bg-[#882629] text-white rounded-lg px-4 py-2 shadow-md">
                  <span className="text-xs font-semibold uppercase tracking-wide">Live Listing · Vancouver</span>
                </div>
              </div>

              {/* Case Details */}
              <div className="p-10 lg:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-[#882629]/10 text-[#882629] rounded-full px-4 py-1.5 text-xs font-semibold mb-6 w-fit">
                  <i className="ri-focus-3-line text-sm"></i>
                  Case Study
                </div>
                <h3 className="font-['Fraunces'] text-2xl lg:text-3xl font-bold text-[#505A5A] mb-4">
                  1-Bedroom Condo, Downtown Vancouver
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed mb-8">
                  A homeowner was self-managing their licensed 1-bedroom condo on Airbnb. After 8 months of inconsistent bookings and a 3.9-star rating, they partnered with us. Within 90 days, we transformed their listing with professional photography, optimized pricing, and 5-star guest experiences.
                </p>

                {/* Before / After Revenue */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-[#F0F0F0] rounded-xl p-6 text-center relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-[#505A5A]/30"></div>
                    <div className="text-xs font-semibold text-[#505A5A]/50 uppercase tracking-wider mb-2">Before</div>
                    <div className="text-3xl font-bold text-[#505A5A] mb-1">$1,850</div>
                    <div className="text-sm text-[#505A5A]/60">avg. monthly revenue</div>
                  </div>
                  <div className="bg-[#882629]/5 rounded-xl p-6 text-center relative overflow-hidden border border-[#882629]/15">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-[#882629]"></div>
                    <div className="text-xs font-semibold text-[#882629] uppercase tracking-wider mb-2">After</div>
                    <div className="text-3xl font-bold text-[#882629] mb-1">$3,420</div>
                    <div className="text-sm text-[#505A5A]/60">avg. monthly revenue</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-[#E8F5E9] rounded-xl px-5 py-3">
                  <i className="ri-arrow-up-circle-fill text-[#2E7D32] text-2xl"></i>
                  <div>
                    <span className="text-lg font-bold text-[#2E7D32]">+85% revenue increase</span>
                    <span className="text-sm text-[#2E7D32]/70 ml-2">within 90 days of onboarding</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Stats Grid */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {[
              {
                stat: '92%',
                label: 'Avg. Occupancy Rate',
                sublabel: 'across managed properties',
                icon: 'ri-calendar-check-line',
              },
              {
                stat: '4.91',
                label: 'Avg. Guest Rating',
                sublabel: 'across all listings',
                icon: 'ri-star-line',
              },
              {
                stat: '+38%',
                label: 'Revenue Uplift',
                sublabel: 'vs. self-managed average',
                icon: 'ri-line-chart-line',
              },
              {
                stat: '<15 min',
                label: 'Avg. Response Time',
                sublabel: 'to guest inquiries',
                icon: 'ri-timer-flash-line',
              },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5 bg-[#882629]/10 rounded-xl">
                  <i className={`${item.icon} text-[#882629] text-2xl`}></i>
                </div>
                <div className="text-3xl font-bold text-[#505A5A] mb-2">{item.stat}</div>
                <div className="text-sm font-semibold text-[#505A5A] mb-1">{item.label}</div>
                <div className="text-xs text-[#505A5A]/50">{item.sublabel}</div>
              </div>
            ))}
          </div>

          {/* Second Case - Quick Snapshot */}
          <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
            <div className="grid lg:grid-cols-5">
              <div className="lg:col-span-2 relative">
                <div className="w-full h-full min-h-[320px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Beautiful%20modern%20furnished%20private%20room%20with%20queen%20bed%20clean%20white%20linens%20bedside%20table%20with%20lamp%20warm%20cozy%20atmosphere%20Airbnb%20guest%20room%20professional%20interior%20photography%20natural%20light%20through%20window%20minimalist%20decor&width=500&height=400&seq=cohost2&orientation=landscape"
                    alt="Private room listing in North Vancouver managed by our co-hosting service"
                    title="North Vancouver private room STR co-hosting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute bottom-4 left-4 bg-[#882629] text-white rounded-lg px-4 py-2 shadow-md">
                  <span className="text-xs font-semibold uppercase tracking-wide">Live Listing · North Vancouver</span>
                </div>
              </div>
              <div className="lg:col-span-3 p-10 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-[#882629]/10 text-[#882629] rounded-full px-4 py-1.5 text-xs font-semibold mb-5 w-fit">
                  <i className="ri-focus-3-line text-sm"></i>
                  Case Study
                </div>
                <h3 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A] mb-3">
                  Private Room, North Vancouver
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed mb-6">
                  A homeowner renting out a spare bedroom had zero reviews and was averaging 6 nights per month. After onboarding, we optimized the listing, implemented competitive pricing, and built a review base. Within 60 days, the room was booked 24+ nights per month.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#F0F0F0] rounded-xl p-4 text-center">
                    <div className="text-xs text-[#505A5A]/50 uppercase tracking-wider mb-1">Before</div>
                    <div className="text-xl font-bold text-[#505A5A]">6</div>
                    <div className="text-xs text-[#505A5A]/60">nights/month</div>
                  </div>
                  <div className="bg-[#882629]/5 rounded-xl p-4 text-center border border-[#882629]/15">
                    <div className="text-xs text-[#882629] uppercase tracking-wider mb-1">After</div>
                    <div className="text-xl font-bold text-[#882629]">24+</div>
                    <div className="text-xs text-[#505A5A]/60">nights/month</div>
                  </div>
                  <div className="bg-[#E8F5E9] rounded-xl p-4 text-center">
                    <div className="text-xs text-[#2E7D32] uppercase tracking-wider mb-1">Revenue</div>
                    <div className="text-xl font-bold text-[#2E7D32]">+310%</div>
                    <div className="text-xs text-[#2E7D32]/70">increase</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <div className="mt-12 bg-white rounded-2xl p-10 shadow-sm text-center max-w-3xl mx-auto">
            <i className="ri-double-quotes-l text-[#882629]/20 text-5xl mb-4 block"></i>
            <p className="text-lg text-[#505A5A] leading-relaxed italic mb-6">
              &ldquo;I was barely breaking even trying to manage my Airbnb myself. Within three months of working with them, my revenue nearly doubled and I haven&apos;t had to answer a single guest message. It&apos;s completely hands-off.&rdquo;
            </p>
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#882629]/10 flex items-center justify-center">
                <span className="text-sm font-bold text-[#882629]">M.L.</span>
              </div>
              <div className="text-left">
                <div className="text-sm font-semibold text-[#505A5A]">M.L.</div>
                <div className="text-xs text-[#505A5A]/60">Homeowner · Downtown Vancouver</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-[#F0F0F0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-6">
            Simple, performance-based pricing
          </h2>
          <div className="bg-white rounded-3xl p-12 shadow-lg">
            <div className="text-5xl font-bold text-[#882629] mb-4">
              20-25%
            </div>
            <p className="text-lg text-[#505A5A]/70 mb-8">
              Commission on bookings • No upfront fees • No hidden costs
            </p>
            <p className="text-base text-[#505A5A]/70 mb-8">
              We only earn when you earn. Our 20-25% commission covers all services including listing management, guest communication, pricing optimization, and coordination of cleaning and maintenance.
            </p>
            <button 
              onClick={scrollToContact}
              className="bg-[#882629] text-white px-10 py-4 rounded-full text-base font-medium hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              Get started with co-hosting
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {[
              {
                question: 'How quickly can I start earning?',
                answer: 'Once your property is licensed and your listing is live, you can typically start receiving bookings within 1-2 weeks. The licensing process varies by city but usually takes 4-8 weeks.'
              },
              {
                question: 'Do I need to provide furniture and amenities?',
                answer: 'Yes, your property should be fully furnished and equipped with essentials like linens, towels, kitchen supplies, and WiFi. We can provide guidance on what guests expect.'
              },
              {
                question: 'What if I want to use my property myself?',
                answer: 'You have full control over your calendar. Simply let us know when you need to block dates, and we will ensure no bookings are accepted during those times.'
              },
              {
                question: 'Who pays for cleaning and maintenance?',
                answer: 'Cleaning costs are typically covered by guest cleaning fees. Maintenance and repairs are the owner\'s responsibility, but we coordinate and manage all service providers on your behalf.'
              },
              {
                question: 'What happens if there is property damage?',
                answer: 'Airbnb provides Host Damage Protection up to $3 million. We document the property condition before and after each stay and file claims on your behalf if damage occurs.'
              },
              {
                question: 'Can I cancel the service anytime?',
                answer: 'Yes, there are no long-term contracts. You can cancel with 30 days\' notice. We will transfer all listing access and guest information back to you.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {item.question}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#882629]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to start earning from your property?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Let us handle the work while you enjoy hands-off rental income. Get started with our co-hosting service today.
          </p>
          <button 
            onClick={scrollToContact}
            className="bg-white text-[#882629] px-10 py-4 rounded-full text-base font-medium hover:bg-[#F0F0F0] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
          >
            Schedule a consultation
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
